import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Stack,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip as MuiTooltip,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Cell,
} from 'recharts';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

interface FunnelStep {
  label: string;
  key: string;
  count: number;
  rate: number;
}

interface ChannelFunnel {
  channel: 'email' | 'sms';
  totalRooms: number;
  steps: FunnelStep[];
}

interface FunnelData {
  period: { startDate: string | null; endDate: string | null };
  totalRooms: number;
  overall: FunnelStep[];
  channels: ChannelFunnel[];
}

interface FunnelAnalyticsProps {
  startDate?: string;
  endDate?: string;
  refreshTrigger: number;
}

const OVERALL_COLORS = ['#1976d2', '#2196f3', '#42a5f5', '#64b5f6', '#90caf9'];
const EMAIL_COLORS = ['#e65100', '#f57c00', '#fb8c00', '#ffa726', '#ffb74d'];
const SMS_COLORS = ['#1b5e20', '#2e7d32', '#388e3c', '#4caf50', '#66bb6a'];

const STEP_DESCRIPTIONS: Record<string, string> = {
  totalRooms: '作成された全ルーム数',
  delivered: 'メールが到達したルーム数（SMS到達は取得対象外）',
  otpIssued: 'OTP（認証コード）が発行されたルーム数',
  login: 'エンドユーザーがOTP認証を完了したルーム数',
  euSent: 'エンドユーザーがメッセージを送信したルーム数',
};

const FunnelAnalytics: React.FC<FunnelAnalyticsProps> = ({
  startDate,
  endDate,
  refreshTrigger,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<FunnelData | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      const response = await fetch(`/api/reports/funnel-analytics?${params}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('APIエラー');
      const result = await response.json();

      if (result.success) {
        setData(result.data);
      } else {
        setError(result.message || 'データ取得に失敗しました');
      }
    } catch (err) {
      setError('ファネル分析データの取得に失敗しました');
      console.error('Funnel analytics fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshTrigger]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!data) return null;

  return (
    <Stack spacing={3}>
      {/* 全体サマリー（ルーム単位） */}
      <Card elevation={2}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TrendingDownIcon color="primary" />
            <Typography variant="h6" fontWeight={600}>
              myTalk利用ファネル（全体）
            </Typography>
            <MuiTooltip title="メール到達はSendGrid確認分のみ。SMS到達は取得対象外" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
            </MuiTooltip>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            ルーム作成 → メール到達 → OTP発行 → ログイン → メッセージ送信 の各ステップ到達率
          </Typography>

          {/* KPI カード */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            {data.overall.map((step, i) => (
              <Card
                key={step.key}
                variant="outlined"
                sx={{
                  flex: '1 1 160px',
                  minWidth: 160,
                  textAlign: 'center',
                  borderColor: OVERALL_COLORS[i],
                  borderWidth: 2,
                }}
              >
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Typography variant="caption" color="text.secondary">
                    {step.label}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color: OVERALL_COLORS[i] }}>
                    {step.count.toLocaleString()}
                  </Typography>
                  <Chip
                    label={`${step.rate}%`}
                    size="small"
                    sx={{
                      mt: 0.5,
                      bgcolor: OVERALL_COLORS[i] + '18',
                      color: OVERALL_COLORS[i],
                      fontWeight: 600,
                    }}
                  />
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* 全体バーチャート */}
          <Box sx={{ height: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data.overall}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    `${value.toLocaleString()} ルーム`,
                    '数',
                  ]}
                  labelFormatter={(label: string) =>
                    STEP_DESCRIPTIONS[data.overall.find((s) => s.label === label)?.key || ''] || label
                  }
                />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {data.overall.map((_, i) => (
                    <Cell key={i} fill={OVERALL_COLORS[i]} />
                  ))}
                  <LabelList
                    dataKey="rate"
                    position="top"
                    formatter={(v: number) => `${v}%`}
                    style={{ fontSize: 12, fontWeight: 600 }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>

      {/* チャネル別ファネル（並列表示） */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
        {data.channels.map((ch) => {
          const isEmail = ch.channel === 'email';
          const colors = isEmail ? EMAIL_COLORS : SMS_COLORS;
          const Icon = isEmail ? EmailIcon : SmsIcon;
          const channelLabel = isEmail ? 'メール経由' : 'SMS経由';

          return (
            <Card key={ch.channel} elevation={2} sx={{ flex: 1, minWidth: 340 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Icon sx={{ color: colors[0] }} />
                  <Typography variant="h6" fontWeight={600}>
                    {channelLabel}
                  </Typography>
                  <Chip
                    label={`${ch.totalRooms} ルーム`}
                    size="small"
                    variant="outlined"
                    sx={{ ml: 'auto', borderColor: colors[0], color: colors[0] }}
                  />
                </Box>

                {/* テーブル */}
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: colors[0] + '0D' }}>
                        <TableCell sx={{ fontWeight: 600 }}>ステップ</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>数</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>率</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>脱落</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {ch.steps.map((step, i) => {
                        const prev = i > 0 ? ch.steps[i - 1].count : step.count;
                        const dropoff = i > 0 ? prev - step.count : 0;
                        const dropoffRate =
                          i > 0 && prev > 0
                            ? Math.round((dropoff / prev) * 1000) / 10
                            : 0;

                        return (
                          <TableRow key={step.key} hover>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box
                                  sx={{
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    bgcolor: colors[i],
                                    flexShrink: 0,
                                  }}
                                />
                                {step.label}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={600}>{step.count.toLocaleString()}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${step.rate}%`}
                                size="small"
                                sx={{
                                  bgcolor: colors[i] + '18',
                                  color: colors[i],
                                  fontWeight: 600,
                                  minWidth: 52,
                                }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              {i > 0 && dropoff > 0 ? (
                                <Typography variant="body2" color="error.main" fontWeight={500}>
                                  -{dropoff} ({dropoffRate}%)
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.disabled">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* バーチャート */}
                <Box sx={{ height: 200 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={ch.steps}
                      margin={{ top: 15, right: 10, left: 0, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip
                        formatter={(value: number) => [
                          `${value.toLocaleString()} ルーム`,
                          '数',
                        ]}
                      />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                        {ch.steps.map((_, i) => (
                          <Cell key={i} fill={colors[i]} />
                        ))}
                        <LabelList
                          dataKey="rate"
                          position="top"
                          formatter={(v: number) => `${v}%`}
                          style={{ fontSize: 10, fontWeight: 600 }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* 注意書き */}
      <Alert severity="info" variant="outlined">
        <Typography variant="body2">
          <strong>集計ルール:</strong>
          「メール到達」はSendGrid Webhook経由で配信確認されたルーム数です。
          SMS到達の取得は対象外のため、SMS経路はOTP発行以降のステップのみ表示しています。
          メール開封・URLクリックは現在未取得のため非表示です。
        </Typography>
      </Alert>
    </Stack>
  );
};

export default FunnelAnalytics;
